'use client'
import dynamic from 'next/dynamic'
import {
  getFeatureDisabledElements,
  getFeatureDisabledRoutes,
  getRoutesForProgressPage,
} from 'utils/next/routes'
import { LayoutProvider } from 'providers/LayoutProvider'
import { HeaderProvider } from 'providers/HeaderProvider'
import styles from 'components/LayoutAppRouter/Layout.module.scss'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
const Alchemer = dynamic(() => import('components/Alchemer'), { ssr: false })
import { useContext, useEffect, useState } from 'react'
const FFAMBanner = dynamic(() => import('components/FFAMBanner'), { ssr: false })
import HeaderContainer from 'components/Header/HeaderContainer/HeaderContainer'
import { getPageMetaTitleForRoute } from 'utils/metadata'
const FooterContainer = dynamic(() => import('components/Footer/FooterContainer/FooterContainer'), {
  ssr: false,
})
const SiteWideBanner = dynamic(() => import('components/SiteWideBanner'), { ssr: false })
import { GlobalProvider } from 'providers/GlobalProvider'
import { ConsentBanner } from 'components/ConsentBanner'

import { FEATURE_TOGGLES, VARIATIONS_UPPER, PFQ_BANNER_ROUTES } from 'constants/featureToggles'
import { isFFAMDisabled } from 'constants/ffam'
import { mainProductConfig } from 'constants/mainProductConfig'
const FooterSticky = dynamic(() => import('components/Footer/FooterSticky/FooterSticky'), {
  ssr: false,
})
import { QuizFlow } from 'components/QuizFlow'
import classNames from 'classnames'
import { ultramarine } from 'components/Fonts/Fonts'
import { isStandaloneRoute } from 'utils/validations'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import logger from '@achieve/cx-logger'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import { getUserInsights } from '@achieve/cx-event-horizon/browser'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { ChatWidget } from 'components/Chatbot/ChatWidget'
import Script from 'next/script'

export default function Layout({
  canonicalLink,
  children,
  emergencyBanners,
  featureToggleObject,
  ffamReferrerContent,
  footer,
  header,
  isFFAMReferrer,
  isMobileUA,
  metaDescription,
  pageConfig,
  pageDisclosure = '',
  pageMetaTitle,
  pageTitle,
  secondaryDisclosures = [],
  showFooter = true,
  showHeader = true,
  alignContentBottom = false,
  consentBanner,
  specificHeaderCtaDataFiltered,
  footerSticky,
  showPhoneNumber,
  siteInfo,
  leadId = '',
  printWebAppSchema = true,
  printLocalBusinessSchema = false,
  associatedProduct,
  publicProcessDotEnv, // Consider just putting publicProcessDotEnv in a Provider instead of passing it as a prop downwards if it's used in multiple components
}) {
  const {
    state: { isPageLoaded },
  } = useContext(AnalyticsContext)

  const { fireAndForget } = useTriggerEvents()

  useEffect(() => {
    // Only fire event for FDR and Achieve website
    if (
      header?.siteEnv === SITE_ENVIRONMENTS.FDR ||
      header?.siteEnv === SITE_ENVIRONMENTS.ACHIEVE
    ) {
      async function fetchInsights() {
        try {
          const res = await getUserInsights()
          // Fire analytics event after insights are set
          if (res?.insights?.visitorInsights?.returningVisitor) {
            fireAndForget({
              event_type: 'track',
              event_action: 'ID Check Segmentation Events - repeat_anonymous_visitor',
              ...handleTrackAndReactEvent(event, {}),
            })
          } else {
            fireAndForget({
              event_type: 'track',
              event_action: 'ID Check Segmentation Events - first_time_anonymous_visitor',
              ...handleTrackAndReactEvent(event, {}),
            })
          }
        } catch (error) {
          logger.error('Error fetching user insights:', { error })
        }
      }
      fetchInsights()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header?.siteEnv, isPageLoaded])

  const disabledRoutes = getFeatureDisabledRoutes(featureToggleObject, header?.siteEnv)
  const disabledElements = getFeatureDisabledElements(featureToggleObject, header?.siteEnv)

  const usesSiteWideBanner = useFeatureFlag(
    FEATURE_TOGGLES[header?.siteEnv]?.ACX_WEB_ENABLE_SITE_WIDE_BANNER
  )

  const PFQBannerExperiment = useFeatureFlag(
    FEATURE_TOGGLES[header?.siteEnv]?.ACX_WEB_EXPERIMENT_PFQ_BANNER
  )

  const [closeFFAMBanner, setCloseFFAMBanner] = useState(false)

  const { dynamicPathname, query, asPath } = useAppRouter()

  const showProgressPage = getRoutesForProgressPage.includes(dynamicPathname)

  /**
   * The object below is used by home-equity-loan-mp to display phone number in the header
   * based on utm_source and utm_content. Phone number data and expected utms are stored
   * in the pageConfig object in the Home Equity Loan MP contentful entry.
   * Implemented by PXP team.
   * Contact: Shreyas Dorle, Brandon Chapman
   */
  let customPhoneData
  if (pageConfig?.customPhoneData) {
    customPhoneData = {
      utm_source: query?.utm_source ?? null,
      utm_content: query?.utm_content ?? null,
      ...pageConfig.customPhoneData,
    }
  }

  const dynamicPageMetaTitle = getPageMetaTitleForRoute(dynamicPathname, pageMetaTitle, pageTitle)

  const handleCloseDialog = () => {
    setCloseFFAMBanner(true)
  }

  const defaultPageMetaTitle = mainProductConfig[header?.siteEnv || 'achieve']?.['MainName']

  const titlePage = dynamicPageMetaTitle
    ? `${dynamicPageMetaTitle} ${dynamicPathname === '/' ? '' : `| ${defaultPageMetaTitle}`}`
    : defaultPageMetaTitle
  const renderEmergencyBanners = () => {
    const list = []
    for (let i = 0; i < emergencyBanners.length; i++) {
      const element = emergencyBanners[i]
      list.push(
        <SiteWideBanner
          title={element?.fields?.title}
          message={element?.fields?.message}
          handleCloseDialog={() => {}}
        />
      )
    }
    return list
  }

  const webpageSchema = {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    name: titlePage,
    url: `${canonicalLink}/`,
    description: metaDescription,
    inLanguage: 'en-US',
  }

  useEffect(() => {
    window.disclosureUrl = siteInfo?.disclosureDataUrl
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const observer = new MutationObserver(() => {
      window.processDisclosures()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div className={styles['page-container']} page-main-variation={header?.siteEnv}>
      {((header?.siteEnv === 'achieve' && asPath === '/l/chat') ||
        (header?.siteEnv === 'website-fdr' && asPath === '/lp/chat/')) && <ChatWidget />}

      <LayoutProvider
        isMobileUA={isMobileUA}
        siteEnv={header?.siteEnv}
        siteInfo={siteInfo}
        leadId={leadId}
      >
        <GlobalProvider pageDisclosure={pageDisclosure || ''}>
          <HeaderProvider>
            <div style={{ position: 'relative' }}>
              {showHeader && (
                <>
                  <HeaderContainer
                    content={header}
                    disabledRoutes={disabledRoutes}
                    disabledElements={disabledElements}
                    customPhoneData={customPhoneData ?? null}
                    showProgressPage={showProgressPage}
                    specificHeaderCtaDataFiltered={specificHeaderCtaDataFiltered}
                    footerSticky={footerSticky}
                    showPhoneNumber={showPhoneNumber}
                    menuMobileLeft={header?.menuLeft}
                  />
                  {PFQBannerExperiment === VARIATIONS_UPPER.VARIATION_A &&
                    PFQ_BANNER_ROUTES.some((prefix) => dynamicPathname.startsWith(prefix)) && (
                      <QuizFlow associatedProduct={associatedProduct} />
                    )}
                  {!isStandaloneRoute(dynamicPathname) && (
                    <ConsentBanner
                      contentfulId={consentBanner?.sys?.id}
                      contentfulVersion={consentBanner?.sys?.revision}
                      siteEnv={header?.siteEnv}
                      publicProcessDotEnv={publicProcessDotEnv}
                    />
                  )}
                </>
              )}

              <div
                className={classNames(styles['max-width-container'], 'group')}
                data-narrow={true}
                data-align-content-bottom={alignContentBottom}
              >
                <main
                  className={classNames(styles.main, ultramarine.variable, 'font-ultramarine')}
                  id="main-layout"
                  data-no-header={!showHeader}
                  data-show-progress={showProgressPage}
                >
                  {!closeFFAMBanner && !isFFAMDisabled() && usesSiteWideBanner && (
                    <FFAMBanner
                      isFFAMReferrer={isFFAMReferrer}
                      ffamReferrerContent={ffamReferrerContent}
                      handleCloseDialog={handleCloseDialog}
                    />
                  )}
                  {emergencyBanners?.length > 0 && renderEmergencyBanners()}
                  {children}
                </main>
              </div>
            </div>

            <FooterSticky
              specificHeaderCtaDataFiltered={specificHeaderCtaDataFiltered}
              headerContent={header}
              content={footerSticky}
            />

            {showFooter && (
              <FooterContainer
                disabledRoutes={disabledRoutes}
                content={footer}
                secondaryDisclosures={secondaryDisclosures}
                data-testid="footer"
                showNav={header?.showNavFooter}
                showFooterSocialIcons={header?.showFooterSocialIcons}
                hiddenNavSection={pageConfig?.hiddenNavSection}
                printWebAppSchema={printWebAppSchema}
                printLocalBusinessSchema={printLocalBusinessSchema}
              />
            )}
          </HeaderProvider>
        </GlobalProvider>
      </LayoutProvider>
      {/* Alchemer survey script */}
      {disabledElements[FEATURE_TOGGLES[header?.siteEnv]?.ACX_WEB_ENABLE_ALCHEMER_SURVEY] && (
        <Alchemer />
      )}
      <script
        id="webPage"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      {siteInfo?.disclosureScriptUrl && (
        <Script src={siteInfo.disclosureScriptUrl} strategy="afterInteractive" />
      )}
    </div>
  )
}
